import React from "react";
import { Box, Typography } from "@mui/material";
import BattingOrderCard from "../fanPage/BattingOrderCard";
import LineupField from "../fanPage/LineupField";
import FormationLineupCard from "./FormationLineupCard";
import SportFormationField from "./SportFormationField";

const FIELD_COPY = {
  mlb: {
    overline: "The field",
    title: "Starting nine",
    hint: "Tap a position to put them on the dirt.",
    viewHint: "Where they play.",
  },
  nba: {
    overline: "The floor",
    title: "Starting five",
    hint: "Tap a spot to fill the five.",
    viewHint: "How they’d start.",
  },
  nfl: {
    overline: "The field",
    title: "How they’d line up",
    hint: "Tap a spot on either side of the ball — or special teams.",
    viewHint: "Where they play.",
  },
};

const FieldPane = ({ copy, roster, resolvedSport, editable, compact, onSelect, onClear }) => (
  <Box>
    {!compact && (
      <>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: "0.16em" }}
        >
          {copy.overline}
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25 }}>
          {copy.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, mb: 2, maxWidth: 520 }}
        >
          {editable ? copy.hint : copy.viewHint}
        </Typography>
      </>
    )}
    {resolvedSport === "mlb" ? (
      <LineupField
        lineup={roster?.players}
        editable={editable}
        compact={compact}
        onSelect={onSelect}
        onClear={onClear}
      />
    ) : (
      <SportFormationField
        sport={resolvedSport}
        slots={roster?.schema?.slots}
        players={roster?.players}
        editable={editable}
        compact={compact}
        onSelect={onSelect}
        onClear={onClear}
      />
    )}
  </Box>
);

const RosterSlotGrid = ({
  roster,
  sport,
  editable = false,
  compact = false,
  battingOrder,
  onSelect,
  onClear,
  onMove,
}) => {
  const resolvedSport = roster?.sport || sport;
  const copy = FIELD_COPY[resolvedSport] || FIELD_COPY.nba;
  const showBatting = !compact && resolvedSport === "mlb" && battingOrder != null;
  const showFormationList = !compact && resolvedSport !== "mlb";
  const showLineup = showBatting || showFormationList;

  if (!showLineup) {
    return (
      <FieldPane
        copy={copy}
        roster={roster}
        resolvedSport={resolvedSport}
        editable={editable}
        compact={compact}
        onSelect={onSelect}
        onClear={onClear}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(320px, 0.9fr)" },
        gap: { xs: 5, lg: 3 },
        alignItems: "start",
      }}
    >
      <FieldPane
        copy={copy}
        roster={roster}
        resolvedSport={resolvedSport}
        editable={editable}
        compact={compact}
        onSelect={onSelect}
        onClear={onClear}
      />
      {showBatting ? (
        <BattingOrderCard
          battingOrder={battingOrder}
          editable={editable}
          compact
          onSelect={onSelect}
          onClear={onClear}
          onMove={onMove}
        />
      ) : (
        <FormationLineupCard
          sport={resolvedSport}
          slots={roster?.schema?.slots}
          players={roster?.players}
          rosterId={roster?.id}
          editable={editable}
          onSelect={onSelect}
          onClear={onClear}
        />
      )}
    </Box>
  );
};

export default RosterSlotGrid;
